import React from 'react';
import { cx } from '@linaria/core';
import { styled } from '@linaria/react';
import { toggle } from 'opticks';
import { BottomSheet } from '@daedalus/atlas/BottomSheet';
import { useDeviceLayout } from '@daedalus/atlas/context/deviceLayout';
import { Divider } from '@daedalus/atlas/Divider';
import { Flex } from '@daedalus/atlas/helpers/Flex';
import { Grid } from '@daedalus/atlas/helpers/Grid';
import { Icon } from '@daedalus/atlas/Icon';
import { PopUp } from '@daedalus/atlas/PopUp';
import { Price } from '@daedalus/atlas/Price';
import { Text } from '@daedalus/atlas/Text';
import { useBrand } from '@daedalus/core/src/_web/brand/hooks/useBrand';
import FormattedMessageWrapper from '@daedalus/core/src/localization/components/FormattedMessage';
export interface Props {
  isOpen: boolean;
  taxes: number;
  hotelFeesTotal: number;
  currencyCode: string;
  chargeableCurrencyCode?: string;
  propertyCurrencyCode?: string;
  onClose: () => void;
}
interface ContentProps {
  taxes: Props['taxes'];
  currencyCode: Props['currencyCode'];
  chargeableCurrencyCode: Props['chargeableCurrencyCode'];
  propertyCurrencyCode: Props['propertyCurrencyCode'];
  hotelTaxes: number;
  withoutPaddings?: boolean;
}
interface TaxSectionProps {
  taxValue: number;
  sectionTitle: JSX.Element;
  sectionSubtitle: JSX.Element;
  currencyCode: Props['currencyCode'];
}
const NUMBER_OF_DECIMALS_FOR_PRICE = 2;
const _exp = /*#__PURE__*/() => Grid;
const ContentContainer = /*#__PURE__*/styled(_exp())({
  name: "ContentContainer",
  class: "c1wgmlxk",
  propsAsIs: true
});
const _exp4 = /*#__PURE__*/() => Price;
const StyledPrice = /*#__PURE__*/styled(_exp4())({
  name: "StyledPrice",
  class: "s45bnwg",
  propsAsIs: true
});
const _exp7 = /*#__PURE__*/() => Divider;
const StyledDivider = /*#__PURE__*/styled(_exp7())({
  name: "StyledDivider",
  class: "s1ioyuc",
  propsAsIs: true
});
const Title = () => <Text variant="titleM">
    <FormattedMessageWrapper id="taxesAndFees.title" defaultMessage="Taxes and fees" />
  </Text>;
const TaxSection = ({
  taxValue,
  currencyCode,
  sectionTitle,
  sectionSubtitle
}: TaxSectionProps) => {
  const isExperimentUpdatePaymentTimeline = toggle('3c85ed3b-update-payment-timeline-v3', false, true);
  const currencyDisplayVariant = isExperimentUpdatePaymentTimeline ? 'code' : undefined;
  return <Grid container spacing="s000" direction="row" wrap="nowrap" alignItems="flex-start" justify="space-between">
      <Grid container spacing="s000" direction="column">
        <Text variant="bodyM">{sectionTitle}</Text>
        <Text variant="bodyS" colorPath="content.neutral.c600">
          {sectionSubtitle}
        </Text>
      </Grid>
      <StyledPrice maximumFractionDigits={NUMBER_OF_DECIMALS_FOR_PRICE} minimumFractionDigits={NUMBER_OF_DECIMALS_FOR_PRICE} value={taxValue} currency={currencyCode} size="md" noStyle currencyDisplayVariant={currencyDisplayVariant} />
    </Grid>;
};
type DisclaimerProps = {
  displayCurrencyCode: string;
  chargeableCurrencyCode?: string;
  propertyCurrencyCode?: string;
  hotelTaxes: number;
};
const _exp10 = /*#__PURE__*/() => Text;
const DisclaimerIcon = /*#__PURE__*/styled(_exp10())({
  name: "DisclaimerIcon",
  class: "dtatbek",
  propsAsIs: true
});
export const Disclaimer = ({
  displayCurrencyCode,
  chargeableCurrencyCode,
  propertyCurrencyCode,
  hotelTaxes
}: DisclaimerProps) => {
  const shouldShowCurrencyConversionDisclaimer = displayCurrencyCode !== chargeableCurrencyCode;
  const shouldShowLocalTaxesDisclaimer = hotelTaxes > 0;
  if (!shouldShowCurrencyConversionDisclaimer && !shouldShowLocalTaxesDisclaimer) {
    return null;
  }
  return <>
      <StyledDivider />
      <Flex.Row gap="s200">
        <DisclaimerIcon>
          <Icon name="Information" colorPath="content.neutral.c600" size="sm" />
        </DisclaimerIcon>
        <Flex.Column gap="s300">
          {shouldShowCurrencyConversionDisclaimer && <Text variant="bodyS" colorPath="content.neutral.c600">
              <FormattedMessageWrapper id="cancellationPenalties.penaltyConversionDisclaimer" defaultMessage="{displayCurrencyCode} price shown is approximate based on the exchange rate of charged currency ({chargeableCurrencyCode}) at the time of booking." values={{
            displayCurrencyCode: displayCurrencyCode,
            chargeableCurrencyCode: chargeableCurrencyCode
          }} />
            </Text>}
          {shouldShowLocalTaxesDisclaimer && <Text variant="bodyS" colorPath="content.neutral.c600">
              <FormattedMessageWrapper id="taxesAndFees.localTaxesCurrencyDisclaimer" defaultMessage="Local taxes are paid at the property in local currency ({propertyCurrency})." values={{
            propertyCurrency: propertyCurrencyCode
          }} />
            </Text>}
        </Flex.Column>
      </Flex.Row>
    </>;
};
const Content = ({
  taxes,
  hotelTaxes,
  currencyCode,
  chargeableCurrencyCode,
  propertyCurrencyCode,
  withoutPaddings
}: ContentProps) => {
  const {
    brandIsVio
  } = useBrand();
  const isExperimentUpdatePaymentTimeline = toggle('3c85ed3b-update-payment-timeline-v3', false, true);
  const currencyDisplayVariant = isExperimentUpdatePaymentTimeline ? 'code' : undefined;
  return <ContentContainer container spacing="s000" direction="column" className={cx(withoutPaddings && 'noPaddings')}>
      <div data-id="taxesAndFeesRow">
        <Grid container spacing="s000" direction="column" gap="s600">
          {taxes > 0 && <TaxSection taxValue={taxes} currencyCode={currencyCode} sectionTitle={<FormattedMessageWrapper id="taxesAndFees.taxesAndFees" defaultMessage="Taxes and fees" />} sectionSubtitle={brandIsVio ? <FormattedMessageWrapper id="taxesAndFees.taxesAndFeesCaption" defaultMessage="Taxes and fees are generally recovery charges that Vio.com pays to its partners (e.g. hotels)." /> : <FormattedMessageWrapper id="taxesAndFeesPopupHeaderText.generic" defaultMessage="Taxes and fees are generally recovery charges paid to partners (e.g. hotels)." />} />}

          {hotelTaxes > 0 && <div data-id="localTaxesRow">
              <TaxSection taxValue={hotelTaxes} currencyCode={currencyCode} sectionTitle={<FormattedMessageWrapper id="taxesAndFees.localTaxes" defaultMessage="Local taxes" />} sectionSubtitle={<FormattedMessageWrapper id="taxesAndFees.localTaxesCaption" defaultMessage="Mandatory local taxes will be collected by the property during your stay." />} />
            </div>}
        </Grid>
      </div>

      <StyledDivider />

      <div data-id="totalTaxesAndFeesRow">
        <Grid spacing="s000" container direction="row" justify="space-between">
          <Text variant="titleM">
            <FormattedMessageWrapper id="taxesAndFees.totalTaxesAndFees" defaultMessage="Total taxes and fees" />
          </Text>
          <Price maximumFractionDigits={NUMBER_OF_DECIMALS_FOR_PRICE} minimumFractionDigits={NUMBER_OF_DECIMALS_FOR_PRICE} value={taxes + hotelTaxes} currency={currencyCode} size="md" currencyDisplayVariant={currencyDisplayVariant} />
        </Grid>
      </div>

      {isExperimentUpdatePaymentTimeline && <Disclaimer displayCurrencyCode={currencyCode} chargeableCurrencyCode={chargeableCurrencyCode} propertyCurrencyCode={propertyCurrencyCode} hotelTaxes={hotelTaxes} />}
    </ContentContainer>;
};
export const TaxesAndFees = ({
  isOpen,
  taxes,
  hotelFeesTotal,
  currencyCode,
  chargeableCurrencyCode,
  propertyCurrencyCode,
  onClose
}: Props) => {
  const {
    isMobile
  } = useDeviceLayout();
  const hotelTaxes = hotelFeesTotal;
  return isMobile ? <BottomSheet isOpen={isOpen} titleBarProps={{
    centerContent: <Title />
  }} onClose={onClose}>
      <Content taxes={taxes} hotelTaxes={hotelTaxes} currencyCode={currencyCode} chargeableCurrencyCode={chargeableCurrencyCode} propertyCurrencyCode={propertyCurrencyCode} withoutPaddings />
    </BottomSheet> : <PopUp isOpen={isOpen} titleBarProps={{
    centerContent: <Title />
  }} closeAction={onClose} width={520}>
      <Content taxes={taxes} hotelTaxes={hotelTaxes} currencyCode={currencyCode} chargeableCurrencyCode={chargeableCurrencyCode} propertyCurrencyCode={propertyCurrencyCode} withoutPaddings />
    </PopUp>;
};

require("./TaxesAndFees.wyw-in-js.css!=!../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./TaxesAndFees.tsx");